import { Box, ListItemButton, ListItemText, styled } from '@mui/material';
import { Category } from '@synergy/models';
import { Fragment, MouseEvent, useEffect, useState } from 'react';
import { Localized } from '@synergy/components';
import { PoolCheckbox } from '@synergy/components-next';

import { PaloDisclosureArrow } from '../palo/palo-disclosure-arrow';
import { categoriesByParentIdSelector } from '../../selectors/category-selectors';
import { getAllCategories } from '../../actions/category-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

interface PoolCategoryTreeItemsProps {
    parentId?: number;
    path: number[];
    selectedIds: number[];
    onSelect: (id: number, path: number[]) => void;
    onDeselect: (id: number) => void;
}

// A row of the tree, as tall as a row of any list in the mask around it. Its
// own 56 came from the theme underneath, whose list items are 48 before the
// content is counted.
const CategoryItem = styled(ListItemButton)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    height: 38,
    paddingTop: 0,
    paddingBottom: 0,
}));

export const PoolCategoryTreeItems = (props: PoolCategoryTreeItemsProps) => {
    const { path, parentId, selectedIds, onSelect, onDeselect } = props;
    const dispatch = useThunkDispatch();
    const categories = useAppSelector(state => categoriesByParentIdSelector(state, parentId));
    const [expanded, setExpanded] = useState<boolean[]>([]);
    const currentPath = parentId ? [...path, parentId] : path;

    useEffect(() => {
        if (!categories) {
            dispatch(getAllCategories(parentId));
        }
    }, [dispatch, categories, parentId]);

    const handleExpandedToggle = (category: Category) => {
        if (!category.hasChildren) {
            toggle(category);
            return;
        }
        const nextExpanded = [...expanded];
        nextExpanded[category.id] = !nextExpanded[category.id];
        setExpanded(nextExpanded);
    };

    const handleSelectToggle = (category: Category) => (e: MouseEvent<unknown>) => {
        e.stopPropagation();
        e.preventDefault();
        toggle(category);
    };

    const toggle = (category: Category) => {
        if (selectedIds.includes(category.id)) {
            onDeselect(category.id);
        } else {
            onSelect(category.id, currentPath);
        }
    };

    return categories?.map(category => {
        const isSelected = selectedIds.includes(category.id);
        const depth = currentPath.length;

        return (
            <Fragment key={category.id}>
                <CategoryItem onClick={() => handleExpandedToggle(category)}>
                    <Box sx={{ minWidth: 44 }}>
                        {category.hasChildren && <PaloDisclosureArrow open={!!expanded[category.id]} />}
                    </Box>
                    <PoolCheckbox
                        style={{ marginLeft: depth * 44 }}
                        variant="round"
                        checked={isSelected}
                        onClick={handleSelectToggle(category)}
                    />
                    <ListItemText>
                        <Localized value={category.translations} />
                    </ListItemText>
                </CategoryItem>
                {expanded[category.id] && (
                    <PoolCategoryTreeItems
                        path={currentPath}
                        parentId={category.id}
                        selectedIds={props.selectedIds}
                        onSelect={onSelect}
                        onDeselect={onDeselect}
                    />
                )}
            </Fragment>
        );
    });
};
